/**
 * OddsChartModal — TradingView-style odds history chart
 * Uses lightweight-charts for interactive zoom/pan/scroll
 * Matches old ABET dashboard ChartModal behavior
 * Prefix: ocm-
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { createChart } from 'lightweight-charts'
import { liveOddsSupabase as supabase } from '../lib/supabase'
import { fetchReceiverOdds } from '../lib/api'
import { getBookBrandColor, getBookLogo } from '../lib/logoMaps'
import { X, Maximize2, Minimize2, TrendingUp } from 'lucide-react'
import './OddsChartModal.css'

const OPTIONAL_SOURCE_TIMEOUT_MS = 5000
const RECEIVER_SOURCE_TIMEOUT_MS = 8000

// Convert American odds to decimal for continuous charting (avoids -100 to +100 gap)
const americanToDecimal = (american) => {
  if (american >= 100) return 1 + (american / 100)
  if (american <= -100) return 1 + (100 / Math.abs(american))
  return 2.0
}

// Convert decimal back to American for display
const decimalToAmerican = (decimal) => {
  if (decimal >= 2.0) return Math.round((decimal - 1) * 100)
  return Math.round(-100 / (decimal - 1))
}

let expansionHistoryPromise = null

function formatOdds(american) {
  if (american == null) return '—'
  return american > 0 ? `+${american}` : `${american}`
}

function buildOutcomeKey(canonicalId, market, selection, handicap) {
  return [
    canonicalId,
    market,
    selection,
    handicap ?? '',
  ].join('|')
}

function withTimeout(promise, timeoutMs, label) {
  let timerId = null

  const timeoutPromise = new Promise((_, reject) => {
    timerId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })

  return Promise.race([
    promise.finally(() => {
      if (timerId) clearTimeout(timerId)
    }),
    timeoutPromise,
  ])
}

async function loadExpansionHistory() {
  if (!expansionHistoryPromise) {
    expansionHistoryPromise = fetch('/data/expansionHistory.generated.json')
      .then((response) => {
        if (!response.ok) throw new Error(`Expansion history unavailable (${response.status})`)
        return response.json()
      })
      .catch((error) => {
        expansionHistoryPromise = null
        throw error
      })
  }

  return expansionHistoryPromise
}

export default function OddsChartModal({
  isOpen,
  onClose,
  canonicalId,
  canonicalIds = null,
  selection,
  selectionVariants = null,
  market,
  handicap,
  currentBooks = null,
  currentTimestamp = null,
}) {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(false)
  const [showAvgLine, setShowAvgLine] = useState(false)

  const chartContainerRef = useRef(null)
  const chartRef = useRef(null)
  const seriesMapRef = useRef({})
  const avgSeriesRef = useRef(null)

  const fetchHistory = useCallback(async () => {
    if (!canonicalId || !selection) return
    setLoading(true)
    setError(null)
    try {
      const merged = new Map()
      const canonicalIdList = Array.from(new Set([...(canonicalIds || []), canonicalId].filter(Boolean)))
      const selectionList = Array.from(new Set([...(selectionVariants || []), selection].filter(Boolean)))
      const seedTimestamp = currentTimestamp || new Date().toISOString()

      Object.entries(currentBooks || {}).forEach(([bookmaker, odds]) => {
        if (odds == null) return
        if (!merged.has(bookmaker)) merged.set(bookmaker, new Map())
        merged.get(bookmaker).set(`${seedTimestamp}|${odds}|seed`, {
          bookmaker,
          timestamp: seedTimestamp,
          odds,
          source: 'board',
        })
      })

      if (merged.size > 0) {
        const seeded = Array.from(merged.values())
          .flatMap((points) => Array.from(points.values()))
          .sort((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime())
        setHistory(seeded)
        setLoading(false)
      }
      const sourceTasks = []

      if (supabase) {
        sourceTasks.push(
          withTimeout((async () => {
            const pageSize = 1000
            const maxRows = 5000
            let from = 0
            let allRows = []
            let done = false

            const historyFloor = new Date(Date.now() - 72 * 3600_000).toISOString()

            while (!done && from < maxRows) {
              let query = supabase
                .from('odds_history')
                .select('bookmaker,american_odds,recorded_at,selection,market_normalized,handicap,previous_odds,odds_movement')
                .eq('market_normalized', market)
                .gte('recorded_at', historyFloor)
                .order('recorded_at', { ascending: true })
                .range(from, from + pageSize - 1)

              query = canonicalIdList.length > 1 ? query.in('canonical_id', canonicalIdList) : query.eq('canonical_id', canonicalIdList[0])
              query = selectionList.length > 1 ? query.in('selection', selectionList) : query.eq('selection', selectionList[0])
              query = handicap == null ? query.is('handicap', null) : query.eq('handicap', handicap)

              const { data, error: err } = await query
              if (err) throw err

              const page = data || []
              allRows = allRows.concat(page)
              done = page.length < pageSize
              from += pageSize
            }

            allRows.forEach((row) => {
              if (!merged.has(row.bookmaker)) merged.set(row.bookmaker, new Map())
              merged.get(row.bookmaker).set(`${row.recorded_at}|${row.american_odds}|history`, {
                bookmaker: row.bookmaker,
                timestamp: row.recorded_at,
                odds: row.american_odds,
                source: 'history',
              })
            })
          })(), OPTIONAL_SOURCE_TIMEOUT_MS, 'Supabase odds history').catch((supabaseError) => {
            console.warn('OddsChartModal Supabase history unavailable:', supabaseError)
          })
        )
      }

      sourceTasks.push(
        Promise.allSettled(
          canonicalIdList.map((candidateCanonicalId) =>
            withTimeout(
              fetchReceiverOdds({
                canonical_id: candidateCanonicalId,
                limit: '1000',
              }),
              RECEIVER_SOURCE_TIMEOUT_MS,
              `Receiver odds for ${candidateCanonicalId}`
            )
          )
        ).then((receiverResults) => {
          receiverResults.forEach((result) => {
            if (result.status !== 'fulfilled' || result.value?.status !== 'ok') return

            ;(result.value.data?.odds || [])
              .filter((row) => row.market_normalized === market)
              .filter((row) => selectionList.includes(row.selection))
              .filter((row) => {
                const rowHandicap = row.handicap == null ? null : Number(row.handicap)
                const targetHandicap = handicap == null ? null : Number(handicap)
                return rowHandicap === targetHandicap
              })
              .forEach((row) => {
                if (!merged.has(row.bookmaker)) merged.set(row.bookmaker, new Map())
                merged.get(row.bookmaker).set(`${row.updated_at}|${row.american_odds}|receiver`, {
                  bookmaker: row.bookmaker,
                  timestamp: row.updated_at,
                  odds: row.american_odds,
                  source: 'receiver',
                })
              })
          })
        })
      )

      sourceTasks.push(
        withTimeout(
          loadExpansionHistory(),
          OPTIONAL_SOURCE_TIMEOUT_MS,
          'Expansion history'
        ).then((expansionHistory) => {
          canonicalIdList.forEach((candidateCanonicalId) => {
            selectionList.forEach((candidateSelection) => {
              const expansionRows = expansionHistory?.[buildOutcomeKey(candidateCanonicalId, market, candidateSelection, handicap)] || []
              expansionRows.forEach((row) => {
                if (!merged.has(row.bookmaker)) merged.set(row.bookmaker, new Map())
                merged.get(row.bookmaker).set(`${row.timestamp}|${row.odds}|${row.source_snapshot || 'expansion'}`, {
                  bookmaker: row.bookmaker,
                  timestamp: row.timestamp,
                  odds: row.odds,
                  source: 'expansion_lab',
                })
              })
            })
          })
        }).catch(() => {
          // Expansion history is optional; production Supabase history remains primary.
        })
      )

      await Promise.allSettled(sourceTasks)

      const normalized = Array.from(merged.values())
        .flatMap((points) => Array.from(points.values()))
        .sort((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime())
      setHistory(normalized)
    } catch (e) {
      console.error('Chart fetch error:', e)
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [canonicalId, canonicalIds, selection, selectionVariants, market, handicap, currentBooks, currentTimestamp])

  useEffect(() => {
    if (isOpen) fetchHistory()
  }, [isOpen, fetchHistory])

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (expanded) setExpanded(false)
        else onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, expanded, onClose])

  // Group history by bookmaker
  const byBookmaker = useMemo(() => {
    const map = {}
    for (const h of history) {
      if (!map[h.bookmaker]) map[h.bookmaker] = []
      map[h.bookmaker].push({
        timestamp: h.timestamp,
        odds: h.odds,
      })
    }
    return map
  }, [history])

  // Current and opening odds per bookmaker
  const currentOdds = useMemo(() => {
    const result = {}
    for (const [book, points] of Object.entries(byBookmaker)) {
      if (points.length > 0) result[book] = points[points.length - 1].odds
    }
    return result
  }, [byBookmaker])

  const openingOdds = useMemo(() => {
    const result = {}
    for (const [book, points] of Object.entries(byBookmaker)) {
      if (points.length > 0) result[book] = points[0].odds
    }
    return result
  }, [byBookmaker])

  // Calculate average line data
  const calculateAverageData = useCallback((bookmakerData) => {
    const tzOffsetSeconds = new Date().getTimezoneOffset() * 60
    const timeMap = new Map()

    Object.values(bookmakerData).forEach(points => {
      points.forEach(p => {
        const time = Math.floor(new Date(p.timestamp).getTime() / 1000) - tzOffsetSeconds
        const decimalOdds = americanToDecimal(p.odds)
        if (decimalOdds > 0) {
          if (!timeMap.has(time)) timeMap.set(time, [])
          timeMap.get(time).push(decimalOdds)
        }
      })
    })

    const avgData = []
    timeMap.forEach((decimalValues, time) => {
      if (decimalValues.length > 0) {
        const avgDecimal = decimalValues.reduce((sum, v) => sum + v, 0) / decimalValues.length
        avgData.push({ time, value: avgDecimal })
      }
    })

    return avgData.sort((a, b) => a.time - b.time)
  }, [])

  // Create/update lightweight-charts chart
  useEffect(() => {
    if (!isOpen || !chartContainerRef.current || Object.keys(byBookmaker).length === 0) return

    // Cleanup old chart
    if (chartRef.current) {
      chartRef.current.remove()
      chartRef.current = null
      seriesMapRef.current = {}
      avgSeriesRef.current = null
    }

    const container = chartContainerRef.current

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight,
      layout: {
        background: { type: 'solid', color: '#0a1628' },
        textColor: '#7A8B9A',
      },
      grid: {
        vertLines: { color: 'rgba(0, 184, 212, 0.1)' },
        horzLines: { color: 'rgba(0, 184, 212, 0.1)' },
      },
      crosshair: {
        mode: 1,
        vertLine: { color: 'rgba(0, 184, 212, 0.5)', width: 1, style: 2, labelBackgroundColor: '#002d72' },
        horzLine: { color: 'rgba(0, 184, 212, 0.5)', width: 1, style: 2, labelBackgroundColor: '#002d72' },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.1, bottom: 0.1 },
        invertScale: true,
      },
      leftPriceScale: { visible: false },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: 'rgba(0, 184, 212, 0.3)',
        rightOffset: 5,
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: { mouseWheel: true, pinch: true },
    })

    chartRef.current = chart
    const tzOffsetSeconds = new Date().getTimezoneOffset() * 60

    // Add series for each bookmaker
    Object.entries(byBookmaker).forEach(([book, points]) => {
      if (!points.length) return

      const color = getBookBrandColor(book)
      const series = chart.addLineSeries({
        color,
        lineWidth: 2,
        priceFormat: {
          type: 'custom',
          formatter: (decimal) => {
            const american = decimalToAmerican(decimal)
            return american > 0 ? `+${american}` : `${american}`
          },
        },
      })

      const sortedPoints = [...points].sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )

      // Add synthetic "now" point to extend lines to current time
      if (sortedPoints.length > 0) {
        sortedPoints.push({
          timestamp: new Date().toISOString(),
          odds: sortedPoints[sortedPoints.length - 1].odds,
        })
      }

      series.setData(sortedPoints.map(p => ({
        time: Math.floor(new Date(p.timestamp).getTime() / 1000) - tzOffsetSeconds,
        value: americanToDecimal(p.odds),
      })))

      seriesMapRef.current[book] = series
    })

    chart.timeScale().fitContent()

    // Resize handler
    const handleResize = () => {
      if (chartRef.current && container) {
        chartRef.current.applyOptions({
          width: container.clientWidth,
          height: container.clientHeight,
        })
      }
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (chartRef.current) {
        chartRef.current.remove()
        chartRef.current = null
        seriesMapRef.current = {}
        avgSeriesRef.current = null
      }
    }
  }, [isOpen, byBookmaker, expanded])

  // Toggle average line
  useEffect(() => {
    if (!chartRef.current || Object.keys(byBookmaker).length === 0) return

    if (showAvgLine && !avgSeriesRef.current) {
      const avgSeries = chartRef.current.addLineSeries({
        color: '#FFFFFF',
        lineWidth: 3,
        lineStyle: 2,
        priceFormat: {
          type: 'custom',
          formatter: (decimal) => {
            const american = decimalToAmerican(decimal)
            return american > 0 ? `+${american}` : `${american}`
          },
        },
        title: 'AVG',
      })

      const avgData = calculateAverageData(byBookmaker)
      avgSeries.setData(avgData)
      avgSeriesRef.current = avgSeries
    } else if (!showAvgLine && avgSeriesRef.current) {
      chartRef.current.removeSeries(avgSeriesRef.current)
      avgSeriesRef.current = null
    }
  }, [showAvgLine, byBookmaker, calculateAverageData])

  if (!isOpen) return null

  const books = Object.keys(byBookmaker).sort()
  const marketLabel = market + (handicap != null ? ` (${handicap > 0 ? '+' : ''}${handicap})` : '')

  // Calculate averages for the table
  const allCurrentOdds = Object.values(currentOdds).filter(v => v != null)
  const avgCurrent = allCurrentOdds.length > 0
    ? decimalToAmerican(allCurrentOdds.map(americanToDecimal).reduce((a, b) => a + b, 0) / allCurrentOdds.length)
    : null
  const allOpenOdds = Object.values(openingOdds).filter(v => v != null)
  const avgOpen = allOpenOdds.length > 0
    ? decimalToAmerican(allOpenOdds.map(americanToDecimal).reduce((a, b) => a + b, 0) / allOpenOdds.length)
    : null

  return (
    <div className="ocm-overlay" onClick={onClose}>
      <div className={`ocm-modal ${expanded ? 'ocm-expanded' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="ocm-header">
          <div className="ocm-header-left">
            <TrendingUp size={16} />
            <span className="ocm-title">{selection}</span>
            <span className="ocm-market-badge">{marketLabel}</span>
          </div>
          <div className="ocm-header-right">
            <button
              className="ocm-btn"
              onClick={() => chartRef.current?.timeScale().fitContent()}
              title="Fit to Data"
            >FIT</button>
            <button className={`ocm-btn ${showAvgLine ? 'ocm-btn-active' : ''}`} onClick={() => setShowAvgLine(!showAvgLine)} title="Toggle Average">
              AVG {showAvgLine ? '✓' : ''}
            </button>
            <button className="ocm-btn" onClick={() => setExpanded(!expanded)} title={expanded ? 'Collapse' : 'Expand'}>
              {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
            <button className="ocm-btn ocm-close" onClick={onClose} title="Close">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="ocm-body">
          {loading ? (
            <div className="ocm-loading">Loading chart data...</div>
          ) : error ? (
            <div className="ocm-error">{error}</div>
          ) : history.length === 0 ? (
            <div className="ocm-empty">No odds history available yet</div>
          ) : (
            <>
              <div ref={chartContainerRef} className="ocm-chart-container" />

              {/* Legend */}
              <div className="ocm-legend">
                {books.map(book => (
                  <span key={book} className="ocm-legend-item">
                    <span className="ocm-legend-dot" style={{ background: getBookBrandColor(book) }} />
                    {book}
                  </span>
                ))}
                {showAvgLine && (
                  <span className="ocm-legend-item">
                    <span className="ocm-legend-dot ocm-legend-avg" />
                    Average
                  </span>
                )}
              </div>

              {/* Odds table footer */}
              <div className="ocm-table">
                <div className="ocm-table-header">
                  <span className="ocm-table-book">Bookmaker</span>
                  <span className="ocm-table-val">Current</span>
                  <span className="ocm-table-val">Open</span>
                  <span className="ocm-table-val">Move</span>
                </div>
                {books.map(book => {
                  const curr = currentOdds[book]
                  const open = openingOdds[book]
                  const move = curr != null && open != null ? curr - open : null
                  const color = getBookBrandColor(book)
                  return (
                    <div key={book} className="ocm-table-row" style={{ borderLeftColor: color }}>
                      <span className="ocm-table-book">
                        <span className="ocm-book-color" style={{ background: color }} />
                        {getBookLogo(book) && (
                          <img src={getBookLogo(book)} alt="" className="ocm-book-logo"
                            onError={e => { e.target.style.display = 'none' }} />
                        )}
                        {book}
                      </span>
                      <span className="ocm-table-val ocm-mono">{formatOdds(curr)}</span>
                      <span className="ocm-table-val ocm-mono ocm-dim">{formatOdds(open)}</span>
                      <span className={`ocm-table-val ocm-mono ${move > 0 ? 'ocm-up' : move < 0 ? 'ocm-down' : ''}`}>
                        {move != null ? (move > 0 ? `+${move}` : `${move}`) : '—'}
                      </span>
                    </div>
                  )
                })}
                {/* Average row */}
                {books.length > 1 && (
                  <div className="ocm-table-row ocm-avg-row" style={{ borderLeftColor: '#FFFFFF' }}>
                    <span className="ocm-table-book">
                      <span className="ocm-book-color" style={{ background: '#FFFFFF' }} />
                      Average
                    </span>
                    <span className="ocm-table-val ocm-mono">{formatOdds(avgCurrent)}</span>
                    <span className="ocm-table-val ocm-mono ocm-dim">{formatOdds(avgOpen)}</span>
                    <span className="ocm-table-val ocm-mono">—</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
